/**
 * SETTINGS MODAL
 *
 * Linear.app-style two-panel layout with vertical sidebar navigation.
 */

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TeamSettingsTab } from "@/features/factory/settings/TeamSettingsTab";
import { GitSettingsTab } from "@/features/factory/settings/GitSettingsTab";
import { UpdatesSettingsTab } from "@/features/factory/settings/UpdatesSettingsTab";
import { WorkerSettingsTab } from "@/features/factory/settings/WorkerSettingsTab";
import { WorkspaceSettingsTab } from "@/features/factory/settings/WorkspaceSettingsTab";
import { isTauri } from "@/hooks/usePlatform";
import { Link } from "wouter";
import { Users, Bot, GitBranch, Download, Cpu, Settings } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface SettingsModalProps {
  workspaceId: string;
  backendWorkspaceId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
}

const NAV_ITEMS = [
  { value: "workspace", label: "Workspace", icon: Settings, needsBackend: false, needsTauri: false },
  { value: "team", label: "Team", icon: Users, needsBackend: true, needsTauri: false },
  { value: "agent-profiles", label: "Agent Profiles", icon: Bot, needsBackend: false, needsTauri: false },
  { value: "git", label: "Integrations", icon: GitBranch, needsBackend: false, needsTauri: false },
  { value: "worker", label: "Local Worker", icon: Cpu, needsBackend: false, needsTauri: true },
  { value: "updates", label: "Updates", icon: Download, needsBackend: false, needsTauri: true },
] as const;

export function SettingsModal({ workspaceId, backendWorkspaceId, open, onOpenChange, defaultTab }: SettingsModalProps) {
  const resolvedDefault = defaultTab || "workspace";

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.needsBackend && backendWorkspaceId == null) return false;
    if (item.needsTauri && !isTauri()) return false;
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl h-[70vh] p-0 gap-0 overflow-hidden"
        hideClose
      >
        <VisuallyHidden>
          <DialogTitle>Settings</DialogTitle>
        </VisuallyHidden>

        <Tabs
          defaultValue={resolvedDefault}
          orientation="vertical"
          className="flex h-full"
        >
          {/* Left sidebar nav */}
          <div className="w-48 flex-shrink-0 border-r border-border flex flex-col bg-muted/30">
            <div className="px-4 pt-5 pb-3">
              <h2 className="text-sm font-semibold text-foreground">Settings</h2>
            </div>

            <TabsList className="flex flex-col items-stretch bg-transparent rounded-none p-0 h-auto gap-0.5 px-2">
              {visibleItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className="justify-start gap-2.5 rounded-md px-3 py-1.5 text-sm font-normal text-muted-foreground shadow-none data-[state=active]:bg-accent data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:border-l-2 data-[state=active]:border-l-primary data-[state=active]:pl-[10px] hover:bg-accent/50 hover:text-foreground transition-colors h-auto"
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {item.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Right content panel */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            {/* Close button */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
              </svg>
              <span className="sr-only">Close</span>
            </button>

            <TabsContent value="workspace" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
              <WorkspaceSettingsTab workspaceId={workspaceId} onCloseSettings={() => onOpenChange(false)} />
            </TabsContent>

            {backendWorkspaceId != null && (
              <TabsContent value="team" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
                <TeamSettingsTab workspaceId={backendWorkspaceId} />
              </TabsContent>
            )}

            <TabsContent value="agent-profiles" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Bot className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium mb-1">Agent Profiles</p>
                <p className="text-sm text-muted-foreground mb-4 max-w-xs">
                  Agent profiles are configured per agent in the World view.
                </p>
                <Link
                  href="/world"
                  className="text-sm text-primary hover:underline underline-offset-2"
                  onClick={() => onOpenChange(false)}
                >
                  Open World view
                </Link>
              </div>
            </TabsContent>

            <TabsContent value="git" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
              <GitSettingsTab workspaceId={workspaceId} backendWorkspaceId={backendWorkspaceId} />
            </TabsContent>

            {isTauri() && (
              <TabsContent value="worker" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
                <WorkerSettingsTab />
              </TabsContent>
            )}

            {isTauri() && (
              <TabsContent value="updates" className="mt-0 p-6 focus-visible:ring-0 focus-visible:ring-offset-0">
                <UpdatesSettingsTab />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
